/**
 * Feeds v2 (/feeds/<feed id>, signed in): a feed built for
 * the request from one cosine-ordered queue over its window, read in waves.
 * Each wave's articles are judged by the one-pass judge; reading stops when
 * under 5% of a wave's stories pass, at the window's position cap (8,000 at
 * 24h, 40,000 at 72h) or at the window's edge. Relevant stories are rank-graded by the decision model and ordered by
 * grade x relevance x time decay. One streamed request per load
 * (`GET /api/v1/feeds/<feed id>/stream`). Verdicts and grades are cached, so a
 * reload first shows what the caches hold (a provisional `stories` event) and
 * then pays only for new articles. Wire types and the pure state the page
 * renders.
 */
import type { FeedMatch, FeedStory } from '../repositories/readerStory';

export interface FeedV2EntityMatch {
  entity: string;
  /** Queued articles naming it. */
  matches: number;
  /** Of those, how many name it in their title or as their first entity. */
  strong: number;
}

export interface FeedV2Info {
  search_id: string;
  name: string;
  description: string;
  description_id: string;
  window_hours: number;
  prompt_id: string;
  model: string;
  grade_prompt_id: string;
  grade_model: string;
  time_decay_exponent: number;
  /** Articles in the window's cosine queue (at most `max_positions`). */
  positions: number;
  max_positions: number;
  waves_planned: number;
  /** Queue positions with a cached verdict when the load began. */
  cached_positions: number;
  /** Leading waves an earlier load already read (judged at once, graded together). */
  warm_waves: number;
  /** The entity the whole brief names, whose queued articles are judged next to the first wave (PRO-1149);
   * null when it names none. Absent from builds of an app task deployed before it. */
  entity_match?: FeedV2EntityMatch | null;
  /** An unsaved search's entry in the reader's search history, which its telemetry names. */
  run_id?: string;
}

export interface FeedV2Candidate {
  id: string;
  title: string | null;
  url: string | null;
  canonical_domain: string | null;
  published_at: string | null;
  similarity: number | null;
  story: string;
}

export interface FeedV2Verdict {
  article_id: string;
  relevance?: number;
  relevant?: boolean;
  cost_usd?: number | null;
  cached?: boolean;
  error: string | null;
}

export type StopReason = 'no_new_stories' | 'pass_rate' | 'max_positions' | 'window_edge' | 'no_verdicts';

export interface FeedV2Wave {
  index: number;
  start: number;
  end: number;
  candidates: FeedV2Candidate[];
  verdicts: FeedV2Verdict[];
  /** Stories in the wave with a verdict, how many of them passed, and how many of those no earlier wave passed. */
  stories: number;
  passing: number;
  new: number;
  stop: StopReason | null;
}

export type RankedStory = FeedStory & {
  rank_grade: number;
  rank_grade_confidence: number;
  rank_relevance: number;
  rank_age_hours: number;
  rank_score: number;
};

export interface UngradedStory {
  id: string;
  title: string | null;
  reason: string | null;
}

/** One line of the stream (`application/x-ndjson`). */
export type FeedV2Event =
  | ({ type: 'feed' } & FeedV2Info)
  | ({ type: 'wave' } & FeedV2Wave)
  | { type: 'stories'; articles: RankedStory[]; ungraded?: UngradedStory[]; provisional?: boolean; append?: boolean }
  | { type: 'heartbeat' }
  | { type: 'done'; read: number; positions: number; stop: StopReason | null }
  | { type: 'error'; detail?: string };

export interface FeedV2State {
  feed: FeedV2Info | null;
  waves: FeedV2Wave[];
  stories: RankedStory[];
  ungraded: UngradedStory[];
  /** The stories shown are the caches' alone, before this load's new articles are judged. */
  provisional: boolean;
  stop: StopReason | null;
  done: boolean;
  error: string | null;
}

export const INITIAL_STATE: FeedV2State = {
  feed: null, waves: [], stories: [], ungraded: [], provisional: false, stop: null, done: false, error: null,
};

/** Fold one stream event into the feed state. An error ends the feed and keeps what was shown. */
export function applyEvent(state: FeedV2State, event: FeedV2Event): FeedV2State {
  switch (event.type) {
    case 'feed': {
      const { type: _type, ...feed } = event;
      return { ...state, feed };
    }
    case 'wave': {
      const { type: _type, ...wave } = event;
      return { ...state, waves: [...state.waves, wave] };
    }
    case 'stories':
      return event.append
        ? { ...state, stories: [...state.stories, ...event.articles] }
        : { ...state, stories: event.articles, ungraded: event.ungraded ?? [], provisional: Boolean(event.provisional) };
    case 'heartbeat':
      return state;
    case 'done':
      return { ...state, stop: event.stop, done: true };
    case 'error':
      return { ...state, error: event.detail || 'Feed failed', done: true };
  }
}

export const STOP_LABELS: Record<StopReason, string> = {
  no_new_stories: 'a wave passed no new story',
  pass_rate: 'under 5% of a wave’s stories passed, adding under 10% to those found',
  max_positions: 'reached the position cap',
  window_edge: 'reached the end of the window',
  no_verdicts: 'a wave got no verdicts',
};

export interface WaveSummary {
  judged: number;
  cached: number;
  relevant: number;
  errors: number;
  costUsd: number;
}

export function summarizeWave(wave: FeedV2Wave): WaveSummary {
  const summary: WaveSummary = { judged: 0, cached: 0, relevant: 0, errors: 0, costUsd: 0 };
  for (const verdict of wave.verdicts) {
    summary.judged += 1;
    if (verdict.error) summary.errors += 1;
    if (verdict.relevant) summary.relevant += 1;
    if (verdict.cached) summary.cached += 1;
    else summary.costUsd += verdict.cost_usd ?? 0;
  }
  return summary;
}

/**
 * Whether a finished build read its queue to a real end and graded what it served, so its stories stand for
 * the feed: not when the judge failed (stop `no_verdicts`: a wave got no usable verdict) nor when at least as
 * many stories went ungraded as were graded (a grading outage). Mirrors
 * `wave_feed_contract.build_complete`, which decides whether a build may replace a stored result.
 */
export function buildComplete(state: Pick<FeedV2State, 'done' | 'error' | 'stop' | 'stories' | 'ungraded'>): boolean {
  if (!state.done || state.error || state.stop === 'no_verdicts') return false;
  return state.ungraded.length === 0 || state.stories.length > state.ungraded.length;
}

/**
 * A feed page's *stories*, each tagged as a member of *feed* (`for_you_searches`, scored by its rank
 * score), as the snapshot feed page's composed stories were: the hide menu offers "Not about <feed>"
 * from it, and the hide is scoped to that feed.
 */
export function withFeedMembership(stories: RankedStory[], feed: FeedMatch): RankedStory[] {
  return stories.map(story => ({ ...story, for_you_searches: [{ id: feed.id, name: feed.name, score: story.rank_score }] }));
}
